/**
 * A family's state, kept current without re-reading it every ten seconds.
 *
 * Two screens now need this -- a child's page shown to a parent, and the full
 * valley left running on a screen -- and a third copy of the same loop would
 * be the point at which one of them quietly drifts. Home keeps its own
 * variant because its polling is CONDITIONAL: it watches only while a device
 * is waiting to be approved, and is deliberately idle the rest of the time.
 * These two watch continuously, because being current is the whole feature.
 *
 * THE BARGAIN, which is the reason `readRevision` exists at all: the poll asks
 * for one number, from one small item -- a single GetItem -- and the full
 * family is re-read only when that number has actually moved. A screen left
 * on all day costs cents a month rather than dollars. Every write that
 * matters bumps the revision, so nothing is missed by watching it instead.
 *
 * `initial` is what the caller already had decrypted, so these screens open
 * on real figures rather than a spinner. Nothing is fetched on mount.
 */

import { useCallback, useEffect, useState } from 'react';
import { loadState, readRevision, type FamilyState } from './state';

/** Matches Home's. Long enough not to hammer a throttled route. */
export const LIVE_POLL_MS = 10_000;

export interface Live {
  state: FamilyState;
  /**
   * The revision moved and the re-read failed, so what is on screen is known
   * to be behind. Distinct from a poll that simply did not answer -- that
   * tells us nothing and is not worth saying.
   */
  stale: boolean;
  refresh: () => Promise<void>;
}

export function useLiveState(
  familyId: string,
  familyKey: CryptoKey,
  initial: FamilyState,
  /** Paused while something else owns the screen, such as a PIN prompt. */
  watching = true,
): Live {
  const [state, setState] = useState<FamilyState>(initial);
  const [stale, setStale] = useState(false);

  const refresh = useCallback(async () => {
    setState(await loadState(familyId, familyKey));
    setStale(false);
  }, [familyId, familyKey]);

  useEffect(() => {
    if (!watching) return;
    let stop = false;
    const timer = window.setInterval(() => {
      void readRevision(familyId)
        .then((now) => {
          if (stop || now === state.revision) return;
          return refresh().catch(() => {
            if (!stop) setStale(true);
          });
        })
        .catch(() => {
          // A refused or dropped poll is not news -- it does not mean the
          // screen is out of date, only that we did not find out. The next
          // one is along shortly.
        });
    }, LIVE_POLL_MS);
    return () => {
      stop = true;
      window.clearInterval(timer);
    };
  }, [familyId, state.revision, refresh, watching]);

  return { state, stale, refresh };
}
